import React, { FC, useState } from "react";

import { useDeleteTask, useGetDag } from "api/api-hooks/airflow-hooks";
import { redirectToAirflowGraphUrl } from "api/ScheduledTasksApi";
import ActionButtons from "components/basic/action-buttons/ActionButtons";
import CollapsibleText from "components/basic/collapsible-text/CollapsibleText";
import DataTable from "components/basic/data-table/DataTable";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { DataTableProps } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { useUserState } from "store/user-store";
import { AirflowDag, AirflowTask } from "types/airflow";
import { TableColumn } from "../measurement-devices/utils/types";
import AirflowDagForm from "./components/AirflowDagForm";
import AirflowTaskForm from "./components/AirflowTaskForm";

const AirflowTasks: React.FC = () => {
  const { dagId } = useParams();
  const { data, isLoading } = useGetDag(dagId ?? "");
  const deleteTask = useDeleteTask();
  const userPermissions = useUserState(state => state.currentUser?.effectivePermissions);

  const canEdit = userPermissions?.includes("AIRFLOW_EDIT");

  if (!dagId) return null;

  const renderActions = (rowData: AirflowTask) => {
    return canEdit ? (
      <ActionButtons
        urlOnLink={`${rowData.taskId.taskId}`}
        onClickDelete={() => deleteTask.mutate({ dagId, taskId: rowData.taskId.taskId })}
      />
    ) : (
      <ActionButtons urlOnLink={`${rowData.taskId.taskId}`} />
    );}

  const renderTaskTags = (streams: string[], prefix: "up" | "down") => (
    <div className="flex flex-column gap-1">
      {streams.length === 0 && <span>{`No ${prefix}stream tasks`}</span>}
      {streams.map(id => (
        <Tag
          key={id}
          value={id}
        />
      ))}
    </div>
  );

  const renderTaskId = (rowData: AirflowTask) => <CollapsibleText text={rowData.taskId.taskId} />;

  const renderStatus = (rowData: AirflowTask) => (
      <div>
        {rowData.isSkipped && <i
          className="pi pi-power-off text-red-600"
          title="Task is skipped (disabled)"
        />}
        {rowData.showStepWarning && <i
          className="pi pi-exclamation-triangle text-orange-600"
          title="Task is executing with more than 1 period steps!"
        />}
      </div>
    );

  const columns: TableColumn<AirflowTask>[] = [
    {
      field: "taskId.taskId",
      header: "Task ID",
      columnKey: "taskId",
      body: renderTaskId,
    },
    {
      field: "title",
      header: "Title",
      columnKey: "title",
    },
    {
      field: "description",
      header: "Description",
      columnKey: "description",
    },
    {
      field: "upstreamTasks",
      header: "Invoked by",
      columnKey: "upstreamTasks",
      body: rowData => renderTaskTags(rowData.upstreamTasks, "up"),
    },
    {
      field: "downstreamTasks",
      header: "Invokes",
      columnKey: "downstreamTasks",
      body: rowData => renderTaskTags(rowData.downstreamTasks, "down"),
    },
    {
      header: "Status",
      columnKey: "status",
      body: renderStatus,
      style: { justifyItems: "center" },
    },
    {
      header: T("word.actions"),
      columnKey: "actions",
      body: renderActions,
      style: { justifyItems: "center" },
    },
  ];

  const tableConfig: DataTableProps<AirflowTask[]> = {
    loading: isLoading,
    header: canEdit && <DagHeader dag={data!} />,
    rows: 25,
  };

  return (
    <Card title="Airflow DAG">
      <DataTable data={data?.tasks ?? []} tableConfig={tableConfig} columns={columns} />
    </Card>
  );
};

export default AirflowTasks;

type DagHeaderProps = {
  dag: AirflowDag;
};
const DagHeader: FC<DagHeaderProps> = ({ dag }) => {
  const [createTaskDialog, setCreateTaskDialog] = useState(false);
  const [editDagDialog, setEditDagDialog] = useState(false);

  return (
    <div className="flex justify-content-between px-3 py-2">
      <div className="flex gap-2">
        <Button
          label={T("action.create-task")}
          icon="pi pi-plus"
          className="p-button-sm"
          onClick={() => setCreateTaskDialog(true)}
        />
        <Button
          label={`${T("action.edit")} DAG`}
          icon="pi pi-pencil"
          className="p-button-sm"
          onClick={() => setEditDagDialog(true)}
        />
      </div>
      <div>
        <a
          href={redirectToAirflowGraphUrl(dag?.dagId)}
          target="_blank"
          rel="noreferrer"
        >
          <Button
            label={`Airflow DAG ${T("word.graph")}`}
            icon="pi pi-link"
            className="p-button-sm"
            onClick={() => {}}
          />
        </a>
      </div>
      <Dialog
        visible={createTaskDialog}
        header="Create new task"
        onHide={() => setCreateTaskDialog(false)}
        style={{ width: "min(100%, 700px)" }}
      >
        <AirflowTaskForm closeDialog={() => setCreateTaskDialog(false)} dag={dag} />
      </Dialog>

      <Dialog
        visible={editDagDialog}
        header="Edit DAG"
        onHide={() => setEditDagDialog(false)}
        style={{ width: "min(100%, 700px)" }}
      >
        <AirflowDagForm closeDialog={() => setEditDagDialog(false)} dag={dag} />
      </Dialog>
    </div>
  );
};